import { useCallback, useMemo } from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import { useHandleShowDialogs } from 'forms/hooks';
import { compose, filter, sort } from 'ramda';
import { isNilOrEmptyString } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { setValue } from '@gef-ui/form-core/stream/transfer/control';
import Autocomplete from '@gef-ui/form-material/Autocomplete';
import { validationDisplayTypes } from '@gef-ui/form-material/constants';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import DatePickerFullWidth from 'components/atoms/GefMui/DatePickerFullWidth';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import { resetFilterForm, submitTaskFilterForm } from 'features/views/taskOverview/actions';
import { FIELDS, TASK_OVERVIEW_FORM_NAME } from 'features/views/taskOverview/form';
import { getCodelistByName, sortAlphabeticallyBy } from 'utils';

export const FilterForm = () => {
	const dispatch = useDispatch();
	const next = useControlNext(TASK_OVERVIEW_FORM_NAME);
	const formData = useFormData();

	const taskTypes = useSelector(getCodelistByName(codelists.taskTypes));
	const finishTypes = useSelector(getCodelistByName(codelists.finishTypes));
	const taskSubtypes = useSelector(getCodelistByName(codelists.taskSubtypes));
	const productDomains = useSelector(getCodelistByName(codelists.productDomains));

	const taskTypesOptions = useMemo(
		() =>
			compose(
				sort(sortAlphabeticallyBy('description')),
				filter((item) => item.informative !== 2), // informative = 2 > lead task
			)(taskTypes),
		[taskTypes],
	);

	const taskSubTypesOptions = useMemo(
		() =>
			compose(
				sort(sortAlphabeticallyBy('description')),
				filter((item) => item.taskTypeIds && item.taskTypeIds.split(',').includes(formData?.taskType)),
			)(taskSubtypes),
		[taskSubtypes, formData?.taskType],
	);

	const finishTypesOptions = useMemo(
		() =>
			compose(
				sort(sortAlphabeticallyBy('description')),
				filter((item) => item.taskTypeId === Number(formData?.taskType) && item.description),
			)(finishTypes),
		[finishTypes, formData?.taskType],
	);

	const handleTaskTypeChange = useCallback(() => {
		next(setValue({ fieldName: FIELDS.taskSubTypeId, value: null }));
		next(setValue({ fieldName: FIELDS.finishTypeId, value: null }));
	}, [next]);

	const handleSubmit = useCallback(() => {
		dispatch(submitTaskFilterForm());
	}, [dispatch]);

	const handleReset = useCallback(() => {
		dispatch(resetFilterForm());
	}, [dispatch]);

	const handleShowDialogsForSearchSubmit = useHandleShowDialogs({
		page: TASK_OVERVIEW_FORM_NAME,
		mapper: {
			atLeastOneOfFilterCriteriaFields: Object.keys(FIELDS),
		},
		continueFn: handleSubmit,
	});

	return (
		<Grid container>
			<Grid size={12}>
				<Grid container spacing={2}>
					<Tooltip title={getLabelMessage('tooltips:policyNo')}>
						<Grid size={2}>
							<TextField
								size="small"
								fieldName={FIELDS.policyNumber}
								label={getLabelMessage('tasksOverview:policyNo')}
								validationDisplay={!formData?.policyNumber ? validationDisplayTypes.HIDDEN : undefined}
								slotProps={{
									htmlInput: {
										maxLength: MaxLengths.POLICY_NO,
									},
								}}
							/>
						</Grid>
					</Tooltip>
					<Grid size={4}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.taskType}
							label={getLabelMessage('tasksOverview:taskType')}
							options={taskTypesOptions}
							onChange={handleTaskTypeChange}
							labelPropName="description"
							validationDisplay={!formData?.taskType ? validationDisplayTypes.HIDDEN : undefined}
						/>
					</Grid>
					<Grid size={4}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.finishTypeId}
							label={getLabelMessage('tasksOverview:finishTypeId')}
							options={finishTypesOptions}
							labelPropName="description"
							disabled={isNilOrEmptyString(formData?.taskType)}
							valuePropName="finishTypeId"
							validationDisplay={!formData?.finishTypeId ? validationDisplayTypes.HIDDEN : undefined}
						/>
					</Grid>
					<Grid size={2}>
						<TextField
							size="small"
							fieldName={FIELDS.id}
							label={getLabelMessage('tasksOverview:id')}
							validationDisplay={!formData?.id ? validationDisplayTypes.HIDDEN : undefined}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.ID,
								},
							}}
						/>
					</Grid>
					<Grid size={6}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.taskSubTypeId}
							label={getLabelMessage('tasksOverview:taskSubType')}
							options={taskSubTypesOptions}
							labelPropName="description"
							disabled={isNilOrEmptyString(formData?.taskType)}
							validationDisplay={!formData?.taskSubTypeId ? validationDisplayTypes.HIDDEN : undefined}
						/>
					</Grid>
					<Grid size={3}>
						<Autocomplete
							size="small"
							fieldName={FIELDS.productDomain}
							label={getLabelMessage('tasksOverview:productArea')}
							options={productDomains}
							labelPropName="description"
							validationDisplay={!formData?.productDomain ? validationDisplayTypes.HIDDEN : undefined}
						/>
					</Grid>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.firstName}
							label={getLabelMessage('tasksOverview:firstName')}
							validationDisplay={!formData?.firstName ? validationDisplayTypes.HIDDEN : undefined}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.NAME,
								},
							}}
						/>
					</Grid>
					<Grid size={3}>
						<TextField
							size="small"
							fieldName={FIELDS.surname}
							label={getLabelMessage('tasksOverview:surname')}
							validationDisplay={!formData?.surname ? validationDisplayTypes.HIDDEN : undefined}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.SURNAME,
								},
							}}
						/>
					</Grid>
					<Grid size={5}>
						<TextField
							size="small"
							fieldName={FIELDS.businessName}
							label={getLabelMessage('tasksOverview:businessName')}
							validationDisplay={!formData?.businessName ? validationDisplayTypes.HIDDEN : undefined}
							slotProps={{
								htmlInput: {
									maxLength: MaxLengths.BUSINESS_NAME,
								},
							}}
						/>
					</Grid>
					<Grid size={2}>
						<DatePickerFullWidth
							fieldName={FIELDS.deadlineFrom}
							label={getLabelMessage('tasksOverview:deadlineFrom')}
							validationDisplay={!formData?.deadlineFrom ? validationDisplayTypes.HIDDEN : undefined}
						/>
					</Grid>
					<Grid size={2}>
						<DatePickerFullWidth
							fieldName={FIELDS.deadlineTo}
							label={getLabelMessage('tasksOverview:deadlineTo')}
							validationDisplay={!formData?.deadlineTo ? validationDisplayTypes.HIDDEN : undefined}
						/>
					</Grid>
					<Grid size={12}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid size={2}>
								<Button variant="outlined" onClick={handleReset} fullWidth>
									{getLabelMessage('common:resetButton')}
								</Button>
							</Grid>
							<Grid size={2}>
								<SubmitButton
									variant="contained"
									onClick={handleSubmit}
									onClickError={handleShowDialogsForSearchSubmit}
									fullWidth
									text={getLabelMessage('common:searchButton')}
								/>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default FilterForm;
